import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';
import { makeListQuery } from '../../common/list-query/list-query';
import { DirectPaymentMethodSchema } from '../direct-payment-method/direct-payment-method';
import { DirectPaymentOrderOriginSchema } from '../direct-payment-order-origin/direct-payment-order-origin';
import { DirectPaymentOrderStatusSchema } from '../direct-payment-order-status/direct-payment-order-status';
import { ProductKeySchema } from '../product-key/product-key';

/**
 * The orders queue's filters (N7).
 *
 * `status` is the one the surface is built around: an operator's working set is
 * `awaiting_confirmation` — the orders somebody is waiting on — and everything
 * else is history they look up rather than work through.
 *
 * `userId` answers the support question this table exists for ("this member says
 * they paid"), and is why the member is named server-side on the row rather than
 * looked up per row by the client (W6).
 *
 * There is **no amount filter and no search**. The amounts are money on a
 * snapshot, not a text column, and "orders over 200" is a report rather than a
 * queue filter; adding a `search` over the note would promise a text index the
 * column does not have. A filter this list does not offer is one an operator
 * cannot be misled by.
 *
 * ## `needsEntitlement` is a FLAG, not a boolean
 *
 * `confirm` claims the order **before** it grants (P23a3: `grantOrExtend`
 * extends a live entitlement rather than refusing, so granting first would hand
 * two racing confirmations sixty days of access for one payment). The safe
 * ordering costs a window: a grant that fails after the claim leaves a
 * `confirmed` row with `entitlement_id` null — visible, rare, and fixable by
 * re-granting. This is what makes it **findable**, so nobody has to notice it.
 *
 * It admits only `'true'` because only `'true'` means anything. Its complement
 * is "every other order", which is the unfiltered list — so a `false` would be a
 * control that narrows nothing while claiming to, and `BooleanParamSchema` would
 * invite exactly that. The spec entry behind it is an `exists` build for the
 * same reason: the predicate is two columns, and a column filter's `isNull`
 * ignores its value, so `false` would have silently behaved like `true`.
 */
export const DirectPaymentOrdersListQuerySchema = makeListQuery({
  userId: UuidSchema.optional(),
  status: DirectPaymentOrderStatusSchema.optional(),
  productKey: ProductKeySchema.optional(),
  method: DirectPaymentMethodSchema.optional(),
  /** F3 — the staff queue's "what did members send us" view. */
  origin: DirectPaymentOrderOriginSchema.optional(),
  needsEntitlement: z.literal('true').optional(),
});

export type DirectPaymentOrdersListQuery = z.infer<typeof DirectPaymentOrdersListQuerySchema>;
